'use client';

import type { PrV2CommentLifecycle } from '@/lib/pr-v2/comment-lifecycle';
import type { PrV2CommentResponse } from '@/lib/pr-v2/types';

import type { PendingComments } from './pending-comments-types';

import { CommentSubjectType } from './pending-comments-types';

import {
  getPrV2CommentLifecycle,
  getPrV2CommentTransitionKey,
} from '@/lib/pr-v2/comment-lifecycle';
import { commentSideToGithubReviewSide } from '@/lib/pr-v2/comment-side';
import { toPendingGithubCommentId } from '@/lib/pr-v2/pending-comment-ids';
import { getStableViewKey } from '@/lib/pr-v2/stable-view-keys';
import { asRecord } from '@/lib/pr-v2/utils/as-record';

export type PrV2ReadModelComment = {
  comment: PrV2CommentResponse;
  lifecycle: PrV2CommentLifecycle;
  displayId: string;
  parentDisplayId: string | null;
  threadId: string;
};

const COMMENT_DISPLAY_PRIORITY = {
  queued: 0,
  submitting: 1,
  persisted: 2,
} as const;

export function mapPendingCommentToPrV2Comment(comment: PendingComments): PrV2CommentResponse {
  const pendingGithubCommentId = toPendingGithubCommentId(comment.id);
  const subjectType = comment.subject_type ?? CommentSubjectType.line;
  const side = commentSideToGithubReviewSide(comment.side);
  const startSide = comment.start_side
    ? commentSideToGithubReviewSide(comment.start_side)
    : undefined;
  const isReply = 'in_reply_to_id' in comment;

  const inReplyToId = isReply
    ? comment.parent_is_pending
      ? toPendingGithubCommentId(comment.in_reply_to_id)
      : String(comment.in_reply_to_id)
    : null;

  return {
    id: pendingGithubCommentId,
    githubCommentId: pendingGithubCommentId,
    commentType: 'review_comment',
    githubReviewId: null,
    body: comment.content,
    path: comment.filename,
    line: subjectType === CommentSubjectType.line ? comment.lineNumber : null,
    position: null,
    inReplyToId,
    userLogin: comment.user.login || null,
    userAvatarUrl: comment.user.avatar_url || null,
    createdAtGithub: comment.created_at,
    updatedAtGithub: comment.created_at,
    raw: {
      node_id: `pending-node:${comment.id}`,
      // This survives promotion into an optimistic submitted comment so the diff can
      // keep one visual thread while the source of truth changes underneath it.
      local_transition_key: pendingGithubCommentId,
      subject_type: subjectType,
      side,
      ...(subjectType === CommentSubjectType.line ? { line: comment.lineNumber } : {}),
      ...(comment.start_line ? { start_line: comment.start_line } : {}),
      ...(startSide ? { start_side: startSide } : {}),
      is_pending_local: true,
    },
  };
}

export function getPrV2CommentDisplayIdentity(comment: PrV2CommentResponse): string {
  return getPrV2CommentTransitionKey(comment) ?? getStableViewKey(comment.githubCommentId);
}

function getPrV2CommentDisplayPriority(comment: PrV2CommentResponse): number {
  const lifecycle = getPrV2CommentLifecycle(comment);
  if (lifecycle === 'persisted') {
    return COMMENT_DISPLAY_PRIORITY.persisted;
  }

  if (lifecycle === 'submitting') {
    return COMMENT_DISPLAY_PRIORITY.submitting;
  }

  return COMMENT_DISPLAY_PRIORITY.queued;
}

function getIdentityLookupKeys(comment: PrV2CommentResponse, displayId: string): string[] {
  const keys = new Set<string>([displayId, comment.githubCommentId, comment.id]);
  const raw = asRecord(comment.raw);
  if (typeof raw?.node_id === 'string' && raw.node_id.length > 0) {
    keys.add(raw.node_id);
  }
  return Array.from(keys).filter((key) => typeof key === 'string' && key.length > 0);
}

function resolveParentDisplayId(
  comment: PrV2CommentResponse,
  displayIdentityByRawId: Map<string, string>,
): string | null {
  if (!comment.inReplyToId) {
    return null;
  }

  return (
    displayIdentityByRawId.get(comment.inReplyToId) ??
    displayIdentityByRawId.get(getStableViewKey(comment.inReplyToId)) ??
    getStableViewKey(comment.inReplyToId)
  );
}

function resolveThreadId(
  displayId: string,
  parentDisplayId: string | null,
  commentByDisplayId: Map<string, PrV2ReadModelComment>,
): string {
  if (!parentDisplayId) {
    return `comment:${displayId}`;
  }

  let currentDisplayId: string | null = parentDisplayId;
  const visitedDisplayIds = new Set<string>();

  while (currentDisplayId) {
    if (visitedDisplayIds.has(currentDisplayId)) {
      break;
    }

    visitedDisplayIds.add(currentDisplayId);
    const parentComment = commentByDisplayId.get(currentDisplayId);
    if (!parentComment) {
      return `comment:${currentDisplayId}`;
    }

    if (!parentComment.parentDisplayId) {
      return `comment:${parentComment.displayId}`;
    }

    currentDisplayId = parentComment.parentDisplayId;
  }

  return `comment:${displayId}`;
}

export function buildPrV2CommentReadModel(params: {
  comments: PrV2CommentResponse[];
  pendingComments?: PendingComments[];
}): PrV2ReadModelComment[] {
  const pendingReviewComments = (params.pendingComments ?? []).map(mapPendingCommentToPrV2Comment);

  // Review submit briefly has both sources alive at once: queued comments from Zustand and
  // promoted optimistic comments from React Query. Collapse them by display identity and
  // prefer the more advanced lifecycle so the thread never disappears or duplicates.
  const sourceComments = [...pendingReviewComments, ...params.comments];
  const mergedComments = new Map<
    string,
    { comment: PrV2CommentResponse; priority: number; displayId: string }
  >();
  const orderedDisplayIds: string[] = [];
  const displayIdentityByRawId = new Map<string, string>();

  for (const comment of sourceComments) {
    const displayId = getPrV2CommentDisplayIdentity(comment);
    for (const identityKey of getIdentityLookupKeys(comment, displayId)) {
      displayIdentityByRawId.set(identityKey, displayId);
    }

    const priority = getPrV2CommentDisplayPriority(comment);
    const existing = mergedComments.get(displayId);
    if (!existing) {
      orderedDisplayIds.push(displayId);
      mergedComments.set(displayId, { comment, priority, displayId });
      continue;
    }

    if (priority >= existing.priority) {
      mergedComments.set(displayId, { comment, priority, displayId });
    }
  }

  const readModelComments = orderedDisplayIds
    .map((displayId) => mergedComments.get(displayId))
    .filter((entry): entry is NonNullable<typeof entry> => entry != null)
    .map((entry) => ({
      comment: entry.comment,
      lifecycle: getPrV2CommentLifecycle(entry.comment),
      displayId: entry.displayId,
      parentDisplayId: resolveParentDisplayId(entry.comment, displayIdentityByRawId),
      threadId: '',
    }));

  const commentByDisplayId = new Map(
    readModelComments.map((comment) => [comment.displayId, comment]),
  );
  for (const readModelComment of readModelComments) {
    readModelComment.threadId = resolveThreadId(
      readModelComment.displayId,
      readModelComment.parentDisplayId,
      commentByDisplayId,
    );
  }

  return readModelComments;
}

export function buildPrV2ReadModelByGithubCommentId(
  comments: PrV2ReadModelComment[],
): Map<string, PrV2ReadModelComment> {
  return new Map(comments.map((comment) => [comment.comment.githubCommentId, comment] as const));
}
